import { useMemo } from 'react'
import {
  GatekeeperPolicyEngine,
  createGatekeeperPolicyEngine,
} from '@labrav/react-components'
import { gatekeeperRemoteAssetBaseUrl } from '../constants'

/**
 * Resolves which policy source to use:
 *
 * - If `VITE_GATEKEEPER_POLICY_SOURCE` is explicitly set to `'local'` or `'s3'`, that wins.
 * - Otherwise:  dev  → `'local'` (WASM loaded from `/auth/gatekeeper` static assets)
 *               prod → `'s3'`   (WASM fetched from `gatekeeperRemoteAssetBaseUrl`)
 */
const resolveGatekeeperPolicySource = (): 'local' | 's3' => {
  const explicit = import.meta.env.VITE_GATEKEEPER_POLICY_SOURCE?.trim()
  if (explicit === 'local' || explicit === 's3') return explicit
  return import.meta.env.DEV ? 'local' : 's3'
}

/**
 * Returns a stable `GatekeeperPolicyEngine` instance.
 *
 * The engine is created once and memoised — it is never recreated unless
 * `gatekeeperRemoteAssetBaseUrl` changes (effectively: never at runtime).
 *
 * Source selection rules (highest priority first):
 *  1. `VITE_GATEKEEPER_POLICY_SOURCE` env var (`'local'` | `'s3'`)
 *  2. Dev build  → `'local'`
 *  3. Prod build → `'s3'`
 */
export const useGatekeeperPolicyEngine = (): GatekeeperPolicyEngine => {
  return useMemo<GatekeeperPolicyEngine>(() => {
    const policySource = resolveGatekeeperPolicySource()

    if (policySource === 'local') {
      return createGatekeeperPolicyEngine({ policySource: 'local' })
    }

    return createGatekeeperPolicyEngine({
      policySource: 's3',
      remoteAssetBaseUrl: gatekeeperRemoteAssetBaseUrl,
    })
  }, [])
}
